// we have to use direct imports to avoid the react dependency
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = _object_without_properties_loose(source, excluded);
    var key, i;
    if (Object.getOwnPropertySymbols) {
        var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceSymbolKeys.length; i++){
            key = sourceSymbolKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {};
    var sourceKeys = Object.keys(source);
    var key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        target[key] = source[key];
    }
    return target;
}
import { AnalyticsReporter } from '@stripe-internal/analytics-js/dist/src/core/AnalyticsReporter';
import { createURL } from '../util';
import { captureWebVitals } from '../webVitals';
import { isProduction } from '../environment';
import { getLayoutDimensions, getSiteAuthParams, getExperimentTokenParams } from './StripeAnalytics';
/**
 * This provider is responsible for reporting events to the legacy stripe analytics endpoint, q.stripe.com.
 * Events reported to this endpoint are not processed by the AEL pipeline and end up in the `analytics.event` table.
 * We are maintaining dual reporting until we can confirm that all downstream consumers of the legacy endpoint have been migrated to use the AEL events.
 */ export class LegacyStripeAnalyticsProvider {
    waitForCID() {
        var _this = this;
        return _async_to_generator(function*() {
            yield _this.analyticsReporter.getClientIDs();
        })();
    }
    ready() {
        var _this = this;
        return _async_to_generator(function*() {
            return !!_this.analyticsReporter;
        })();
    }
    trackPage({ id, title, path_with_qs: pathWithQS, referrer }) {
        var _this = this;
        return _async_to_generator(function*() {
            _this.analyticsReporter.viewed(id, _object_spread({
                title,
                mkt_path: pathWithQS,
                page: pathWithQS,
                referrer,
                from_mkt_srv: true
            }, getLayoutDimensions(), _this.getStripeLocale(), getSiteAuthParams(), getExperimentTokenParams()));
        })();
    }
    trackEvent(event) {
        var _this = this;
        return _async_to_generator(function*() {
            const { category, action, label, url } = event;
            // Get pathWithQS from passed url for the page param
            const pageUrl = url || window.location.href;
            const newUrl = createURL(pageUrl);
            const pathWithQS = newUrl ? newUrl.pathname + newUrl.search : window.location.pathname + window.location.search;
            _this.analyticsReporter.action(action, _object_spread({
                category,
                label,
                value: event.value,
                page: pathWithQS,
                from_mkt_srv: true
            }, getLayoutDimensions(), _this.getStripeLocale(), getSiteAuthParams(), getExperimentTokenParams()));
        })();
    }
    trackVitals(vitals) {
        var _this = this;
        return _async_to_generator(function*() {
            const metrics = yield captureWebVitals(vitals);
            const pageUrl = window.location.href;
            const newUrl = createURL(pageUrl);
            const pathWithQS = newUrl ? newUrl.pathname + newUrl.search : window.location.pathname + window.location.search;
            _this.analyticsReporter.action('web-vitals', _object_spread({
                page: pathWithQS,
                from_mkt_srv: true
            }, metrics, getLayoutDimensions(), getSiteAuthParams(), _this.getStripeLocale(), getExperimentTokenParams()));
        })();
    }
    setExperimentTreatment(_param) {
        var _this = this;
        return _async_to_generator(function*() {
            var { trackingMetadata: { path_with_qs: pathWithQS, title, referrer }, variantId, experimentId } = _param, remainingMetadata = _object_without_properties(_param, [
                "trackingMetadata",
                "variantId",
                "experimentId"
            ]);
            // Note that `viewed_name` is automatically added by analytics.js to match the last sent value.
            _this.analyticsReporter.action('experiment.exposed', _object_spread({
                title,
                referrer,
                page: pathWithQS,
                mkt_path: pathWithQS,
                fromMktSrv: true,
                experimentId,
                variantId
            }, remainingMetadata, getLayoutDimensions(), getSiteAuthParams(), _this.getStripeLocale(), getExperimentTokenParams()));
        })();
    }
    getStripeLocale() {
        const { siteLanguage, siteLocale, unglobalizedPath } = this.getLocaleInfo();
        return {
            stripe_locale: `${siteLanguage}-${siteLocale}`,
            unglobalized_path: unglobalizedPath
        };
    }
    constructor(environment, pageViewToken, getLocaleInfo, aelClientId, aelNamespace){
        _define_property(this, "getLocaleInfo", void 0);
        _define_property(this, "analyticsReporter", void 0);
        this.getLocaleInfo = getLocaleInfo;
        this.analyticsReporter = new AnalyticsReporter({
            clientId: aelClientId || 'mkt-srv',
            namespace: aelNamespace,
            inProduction: isProduction(environment),
            botDetection: true,
            pageviewtoken: pageViewToken,
            // namespace not included for backwards compatibility
            // send to legacy stripe analytics endpoint
            overrideTrackingUrl: {
                prod: 'https://q.stripe.com',
                qa: 'https://q.stripe.com'
            }
        });
        window.DeprecatedAnalyticsReporter = this.analyticsReporter;
    }
}

//# sourceMappingURL=LegacyStripeAnalytics.js.map